<template>
  <v-row class="section-container">
    <v-col cols="12" class="header-with-select-box">
      <v-row align="center" justify="space-between">
        <v-col cols="6">
          <h3 class="text-primary weight-600">Submission Fax Form</h3>
        </v-col>
        <v-col v-if="totalPages > 1" cols="6" class="text-end pdf-pager">
          <v-btn v-if="currentPage > 1" @click="prevPage">←</v-btn>
          <v-btn v-if="currentPage < totalPages" class="ml-5" @click="nextPage">→</v-btn>
        </v-col>
      </v-row>
      <v-alert
        v-if="props.formButtonClicked && !isFormValid()"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4 form-validation-alert">
        Please fill all required fields before proceeding.
      </v-alert>
      <v-row>
        <v-col cols="12" class="d-flex justify-center">
          <div ref="fitContainer" class="pdf-fit" :style="fitHeight != null ? { height: fitHeight + 'px' } : {}">
            <div
              class="position-relative pdf-fit__inner"
              :style="{ transform: `scale(${fitScale})`, transformOrigin: 'top center' }">
              <canvas ref="pdfCanvas" width="100%" class="d-block v-btn--readonly"></canvas>
              <div
                v-for="field in overlayFields.filter((f) => f.page === currentPage)"
                :key="field.id"
                :style="fieldStyle(field)">
                <template v-if="field.type === 'checkbox'">
                  <input
                    class="lh--editable-pdf"
                    :class="{ 'error-field': isFieldError(field) }"
                    type="checkbox"
                    :disabled="props.disabled"
                    :checked="field.value !== null"
                    @change="field.value = $event.target.checked ? 'X' : null" />
                </template>
                <template v-else>
                  <input
                    v-model="field.value"
                    type="text"
                    class="lh--editable-pdf"
                    :class="{ 'error-field': isFieldError(field) }"
                    :disabled="props.disabled" />
                </template>
                <div v-if="isFieldError(field)" class="error-message">This field is required</div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineExpose } from "vue";
import * as pdfjs from "pdfjs-dist";
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).toString();

const props = defineProps({
  pdfForm: {
    type: Object,
    required: true,
  },
  formButtonClicked: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const pdfCanvas = ref(null);
const overlayFields = ref([]);
const scale = 96 / 72;
const currentPage = ref(1);
const totalPages = ref(1);
let pdfDoc = null;

// Fit the native-width PDF canvas (+ its px-positioned overlay inputs) to the
// container by scaling their shared wrapper, so it never overflows on mobile.
const fitContainer = ref(null);
const fitScale = ref(1);
const fitHeight = ref(null);
let resizeObserver = null;

function computeFit() {
  const canvas = pdfCanvas.value;
  const container = fitContainer.value;
  if (!canvas || !container || !canvas.width || !container.clientWidth) return;
  const s = Math.min(1, container.clientWidth / canvas.width);
  const h = canvas.height * s;
  if (Math.abs(s - fitScale.value) < 0.0005 && fitHeight.value != null && Math.abs(h - fitHeight.value) < 0.5) return;
  fitScale.value = s;
  fitHeight.value = h;
}

onMounted(async () => {
  if (!props.pdfForm || !props.pdfForm.file) {
    return;
  }
  const pdfData = convertDataUriToArrayBuffer(props.pdfForm.file);
  const loadingTask = pdfjs.getDocument({ data: pdfData });
  pdfDoc = await loadingTask.promise;
  totalPages.value = pdfDoc.numPages;

  overlayFields.value = props.pdfForm.objects.map((obj) => {
    return {
      id: obj.id,
      page: obj.pages + 1,
      x: obj.x * scale,
      y: obj.y * scale,
      width: obj.width * scale,
      height: obj.height * scale,
      value: obj.text,
      type: obj.type,
    };
  });

  await renderPage(currentPage.value);

  if (fitContainer.value) {
    resizeObserver = new ResizeObserver(() => computeFit());
    resizeObserver.observe(fitContainer.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

// Validation logic
const isFieldError = (field) => {
  const fieldId = field.id?.toLowerCase() ?? "";
  // Signature-date boxes are system-managed: the backend stamps the send date into every field
  // whose id carries both "signature" and "_date" on each render (apply_signature_date_to_pdf in
  // portal/logic/ai/fax_responder.py — keep the two rules in sync), so the user is never asked to
  // fill one. Checked first because "prescriber_signature_date" also matches the signature rule
  // below, which used to demand the user type into the date box as if it were the signature line.
  if (fieldId.includes("signature") && fieldId.includes("_date")) {
    return false;
  }
  const isSignatureField = fieldId.includes("prescriber_signature");
  const isDateField = fieldId === "treatment_start_date";
  const isFrequencyField = fieldId.includes("frequency");

  if (isFrequencyField) {
    // Frequency field must be not more than 35 characters if it has a value
    return field.value && field.value.length > 35;
  }
  // Check signature fields
  if (isSignatureField) {
    return !field.value || field.value.trim() === "";
  }

  // Check date fields with format validation
  if (isDateField) {
    if (!field.value || field.value.trim() === "") {
      return true; // Empty field is invalid
    }

    // Validate date format mm/dd/yyyy
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    return !dateRegex.test(field.value.trim());
  }

  return false;
};

const getValidationErrors = () => {
  return overlayFields.value.filter((field) => isFieldError(field)).map((field) => field.id);
};

const isFormValid = () => {
  return getValidationErrors().length === 0;
};

async function renderPage(pageNumber) {
  const page = await pdfDoc.getPage(pageNumber);
  const viewport = page.getViewport({ scale: scale });

  const canvas = pdfCanvas.value;
  if (!canvas) {
    return;
  }
  const context = canvas.getContext("2d");
  canvas.height = viewport.height;
  canvas.width = viewport.width;

  const renderTask = page.render({
    canvasContext: context,
    viewport: viewport,
  });
  await renderTask.promise;
  computeFit();
}

async function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    await renderPage(currentPage.value);
  }
}

async function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
    await renderPage(currentPage.value);
  }
}

function fieldStyle(field) {
  return {
    position: "absolute",
    top: field.y + "px",
    left: field.x + "px",
    width: field.width + "px",
    height: field.height + "px",
  };
}

function convertDataUriToArrayBuffer(dataURI) {
  const base64String = dataURI.split(",")[1];
  const raw = window.atob(base64String);
  const array = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) {
    array[i] = raw.charCodeAt(i);
  }
  return array.buffer;
}

function mergeOverlayToPdfForm() {
  overlayFields.value.forEach((overlayField) => {
    const matchingObject = props.pdfForm.objects.find((obj) => obj.id === overlayField.id);
    if (matchingObject) {
      matchingObject.text = overlayField.value;
    }
  });
}

defineExpose({
  mergeOverlayToPdfForm,
  getFormData: () => overlayFields.value,
  validateAll: () => isFormValid(),
  getValidationErrors,
});
</script>

<style scoped>
.pdf-fit {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
}

.pdf-fit__inner {
  display: inline-block;
  vertical-align: top;
}

.error-field {
  border: 2px solid #f44336;
  background-color: #ffebee;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
}
</style>
